import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import FusionCharts from "fusioncharts";
import FusionMaps from "fusioncharts/fusioncharts.maps";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
import USA from "fusionmaps/maps/fusioncharts.usa";

import React from "react";
import ReactFC from "react-fusioncharts";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { KeyNumberValue, KeyValue, Query } from "../../interfaces";
import HeatBoxIcon from "./HeatBoxIcon";
import { licenseKey } from "./license";

const useStyles = makeStyles(theme => ({
  summaryDepositsTextBox: {
    position: "relative",
    color: "white",
    background: theme.palette.primary.main,
    fontSize: "1.25rem",
    padding: ".5rem",
    whiteSpace: "nowrap",
  },
  cardDisplay: {
    "border": `1px solid ${theme.palette.primary.main}`,
    "borderRadius": 8,
    "color": theme.palette.primary.main,
    "& .MuiTypography-body1": {
      fontSize: ".875rem",
      lineHeight: "1rem",
      paddingBottom: ".5rem",
    },
    "& .MuiCardContent-root": {
      padding: "1rem .5rem .5rem .5rem",
    },
  },
  statesHeading: {
    position: "relative",
    color: theme.palette.primary.main,
    fontSize: "1rem",
    fontWeight: "bolder",
    paddingLeft: 0,
    textAlign: "left",
    paddingBottom: 7,
  },
  territory: {
    "& .MuiTypography-body2": {
      fontSize: ".875rem",
      whiteSpace: "nowrap",
      paddingTop: 2,
      fontWeight: 400,
      paddingBottom: 0,
      cursor: "default",
    },
  },
}));

interface Props {
  appState: Query;
  locationsOnly?: boolean;
  locationsData?: KeyValue[];
  updateFilter?: (field: string, value: string) => void;
}

ReactFC.fcRoot(FusionCharts, FusionMaps, USA, FusionTheme);

(FusionCharts as any).options["license"]({
  key: licenseKey,
  creditLabel: false,
});

export const handleMouseOver = (
  state: string,
  stateAbbr: string,
  stateElement: KeyValue,
  summaryOfDepositsElement: KeyValue,
  summaryOfDepositsByState: KeyValue,
  institutionsElement: KeyValue,
  countOfInstitutionsByState: KeyValue,
  branchOfficesElement: KeyValue,
  countOfBranchOfficesByState: KeyValue,
) => {
  if (stateElement && stateElement.current) {
    stateElement.current.innerText = state;
  }
  if (
    summaryOfDepositsElement &&
    summaryOfDepositsElement.current &&
    stateAbbr
  ) {
    summaryOfDepositsElement.current.innerText = summaryOfDepositsByState[
      stateAbbr
    ]
      ? `$${summaryOfDepositsByState[stateAbbr].toLocaleString()}`
      : "$0";
  }
  if (institutionsElement && institutionsElement.current && stateAbbr) {
    institutionsElement.current.innerText = countOfInstitutionsByState[
      stateAbbr
    ]
      ? `${countOfInstitutionsByState[stateAbbr].toLocaleString()}`
      : "0";
  }
  if (branchOfficesElement && branchOfficesElement.current && stateAbbr) {
    branchOfficesElement.current.innerText = countOfBranchOfficesByState[
      stateAbbr
    ]
      ? `${countOfBranchOfficesByState[stateAbbr].toLocaleString()}`
      : "0";
  }
  return {
    state: stateElement,
    summary: summaryOfDepositsElement,
    institutions: institutionsElement,
    branches: branchOfficesElement,
  };
};

export const handleMouseOut = (
  stateElement: KeyValue,
  summaryOfDepositsElement: KeyValue,
  totalSummaryOfDeposits: number,
  institutionsElement: KeyValue,
  totalInstitutions: number,
  branchOfficesElement: KeyValue,
  totalBranchOffices: number,
) => {
  if (stateElement && stateElement.current) {
    stateElement.current.innerText = "All States and Territories";
  }
  if (summaryOfDepositsElement && summaryOfDepositsElement.current) {
    summaryOfDepositsElement.current.innerText = `$${totalSummaryOfDeposits.toLocaleString()}`;
  }
  if (institutionsElement && institutionsElement.current) {
    institutionsElement.current.innerText = `${totalInstitutions.toLocaleString()}`;
  }
  if (branchOfficesElement && branchOfficesElement.current) {
    branchOfficesElement.current.innerText = `${totalBranchOffices.toLocaleString()}`;
  }

  return {
    state: stateElement,
    summary: summaryOfDepositsElement,
    institutions: institutionsElement,
    branches: branchOfficesElement,
  };
};

export default function USAMap({
  appState,
  locationsOnly,
  locationsData,
  updateFilter,
}: Props) {
  const colorrange = {
    minvalue: 0,
    startlabel: "Low",
    endlabel: "High",
    code: "#FFFFA2",
    gradient: "1",
    color: [
      {
        maxvalue: 1000000000000,
        code: "#ED4630",
        displayValue: "Median",
      },
      {
        maxvalue: 3000000000000,
        code: "#B2D1FD",
        displayValue: "High",
      },
    ],
  };

  const colorrangeLocationsOnly = {
    minvalue: 0,
    startlabel: "Low",
    endlabel: "High",
    code: "#FFFFA2",
    gradient: "1",
    color: [
      {
        maxvalue: 300,
        code: "#ED4630",
        displayValue: "Median",
      },
      {
        maxvalue: 1000,
        code: "#B2D1FD",
        displayValue: "High",
      },
    ],
  };
  const stateElement = React.useRef<HTMLInputElement>(
    document.createElement("state") as HTMLInputElement,
  );
  const institutionsElement = React.useRef<HTMLInputElement>(null);
  const branchOfficesElement = React.useRef<HTMLInputElement>(null);
  const [previousYear, setPreviousYear] = React.useState(1994);
  const summaryOfDepositsElement = React.useRef<HTMLInputElement>(null);
  const [mapData, setMapData] = React.useState<KeyValue[]>([]);
  const [
    summaryOfDepositsByState,
    setSummaryOfDepositsByState,
  ] = React.useState<KeyValue>({});
  const [totalInstitutions, setTotalInstitutions] = React.useState(0);
  const [totalBranchOffices, setTotalBranchOffices] = React.useState(0);
  const [totalSummaryOfDeposits, setTotalSummaryOfDeposits] = React.useState(0);
  const initialCountBy: KeyValue = {};
  const [
    countOfInstitutionsByState,
    setCountOfInstitutionsByState,
  ] = React.useState(initialCountBy);
  const [
    countOfBranchOfficesByState,
    setCountOfBranchOfficesByState,
  ] = React.useState(initialCountBy);
  const classes = useStyles();
  const [legendRangeMinValue, setLegendRangeMinValue] = React.useState(0);
  const [legendRangeMaxValue, setLegendRangeMaxValue] = React.useState(
    38000000000000,
  );
  const usStatesAndTerritories = [
    { abbr: "AS", label: "American Samoa" },
    {
      abbr: "FM",
      label: "Federated States of Micronesia",
      altLabel: "F.S. of Micronesia",
    },
    { abbr: "DC", label: "District of Columbia" },
    { abbr: "GU", label: "Guam" },
    { abbr: "MH", label: "Marshall Islands" },
    { abbr: "MP", label: "Northern Mariana Islands" },
    { abbr: "PW", label: "Palau" },
    { abbr: "PR", label: "Puerto Rico" },
    { abbr: "VI", label: "US Virgin Islands" },
  ];

  const handleRangeUpdated = (eventObj: KeyValue) => {
    setLegendRangeMinValue(eventObj.data.minValue);
    setLegendRangeMaxValue(eventObj.data.maxValue);
  };

  React.useEffect(() => {
    //gets total count of summary of deposits (total and by state)
    if (
      // !locationsOnly &&
      appState.filters.lastYear &&
      (Object.keys(summaryOfDepositsByState).length === 0 ||
        Object.keys(totalSummaryOfDeposits).length === 0) &&
      appState.filters.lastYear !== previousYear
    ) {
      let isMounted = true;
      const sumDepositsDataByState: KeyNumberValue = {};
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?agg_by=STALPBR&agg_sum_fields=DEPSUMBR&filters=YEAR:${
            appState.filters.lastYear
          }&agg_limit=100`,
        )
          .then(response => {
            if (isMounted) {
              const data = response.data;
              for (const dataElem of data) {
                const count = dataElem["sum_DEPSUMBR"];
                const state = dataElem["STALPBR"];
                sumDepositsDataByState[state] = count;
              }
              const values = Object.values(sumDepositsDataByState);
              const sum = values.reduce((accumulator, value) => {
                return accumulator + value;
              }, 0);
              setTotalSummaryOfDeposits(sum);
              if (stateElement && stateElement.current) {
                stateElement.current.innerText = "All States and Territories";
              }
              if (
                summaryOfDepositsElement &&
                summaryOfDepositsElement.current
              ) {
                summaryOfDepositsElement.current.innerText = `$${sum.toLocaleString()}`;
              }
              setSummaryOfDepositsByState(sumDepositsDataByState);
              const respMapData = data.map((state: KeyValue) => {
                return { id: state.STALPBR, value: 0 };
              });
              for (const elem of respMapData) {
                const statesAbbr = elem["id"];
                if (sumDepositsDataByState[statesAbbr]) {
                  elem["value"] = sumDepositsDataByState[statesAbbr] * 1000;
                }
              }
              setMapData(respMapData);
              setPreviousYear(appState.filters.lastYear);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
      // } else if (locationsOnly && locationsData) {
      //   let tempData = [...locationsData];

      //   setMapData(tempData);
      //   if (branchOfficesElement?.current) {
      //     const locationsTotal = locationsData.reduce((a, c) => a + c.value, 0);

      //     branchOfficesElement.current.innerText = locationsTotal.toLocaleString();
      //     setTotalBranchOffices(locationsTotal);
      //   }
      //   const countOfBranchOfficesByStateObj: KeyNumberValue = {};
      //   locationsData.forEach(
      //     bank => (countOfBranchOfficesByStateObj[bank.id] = bank.value),
      //   );

      //   setCountOfBranchOfficesByState(countOfBranchOfficesByStateObj);

      //   if (stateElement?.current) {
      //     stateElement.current.innerText = "All States and Territories";
      //   }
    }
  }, [
    // mapData,
    summaryOfDepositsByState,
    appState.filters.lastYear,
    totalSummaryOfDeposits,
    previousYear,
    locationsData,
    // locationsOnly,
  ]);

  React.useEffect(() => {
    //get total count of institutions
    if (
      // !locationsOnly &&
      Object.keys(totalInstitutions).length === 0 &&
      appState.filters.lastYear
    ) {
      let isMounted = true;
      let institutions = 0;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?&agg_by=CERT&filters=YEAR:${
            appState.filters.lastYear
          } AND BKMO:1&limit=1`,
        )
          .then(response => {
            if (isMounted) {
              institutions = response.totals.count;
              setTotalInstitutions(institutions);
              if (institutionsElement && institutionsElement.current) {
                institutionsElement.current.innerText = institutions.toLocaleString();
              }
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [totalInstitutions, appState.filters.lastYear, locationsOnly]);

  React.useEffect(() => {
    //get total count of branch offices
    if (
      // !locationsOnly &&
      Object.keys(totalBranchOffices).length === 0 &&
      appState.filters.lastYear
    ) {
      let isMounted = true;
      let branchOffices = 0;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?&agg_by=UNINUMBR&filters=YEAR:${
            appState.filters.lastYear
          }&agg_limit=1`,
        )
          .then(response => {
            if (isMounted) {
              branchOffices = response.totals.count;
              setTotalBranchOffices(branchOffices);
              if (branchOfficesElement && branchOfficesElement.current) {
                branchOfficesElement.current.innerText = branchOffices.toLocaleString();
              }
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [totalBranchOffices, appState.filters.lastYear, locationsOnly]);

  React.useEffect(() => {
    //get count of institutions and branch offices by state
    if (
      (// !locationsOnly &&
      Object.keys(countOfInstitutionsByState).length === 0 ||
        Object.keys(countOfBranchOfficesByState).length === 0) &&
      appState.filters.lastYear
    ) {
      let isMounted = true;
      const countOfInstitutionsByStateObj: KeyNumberValue = {};
      const countOfBranchOfficesByStateObj: KeyNumberValue = {};
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?&agg_by=STALPBR&filters=YEAR:${
            appState.filters.lastYear
          }&get_unique=CERT&agg_limit=100`,
        )
          .then(response => {
            if (isMounted) {
              const data = response.data;
              for (const dataElem of data) {
                const countInstByState = dataElem["unique_count"];
                const countBranchesByState = dataElem["count"];
                const state = dataElem["STALPBR"];
                countOfInstitutionsByStateObj[state] = countInstByState;
                countOfBranchOfficesByStateObj[state] = countBranchesByState;
              }
              setCountOfInstitutionsByState(countOfInstitutionsByStateObj);
              setCountOfBranchOfficesByState(countOfBranchOfficesByStateObj);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState.filters.lastYear,
    countOfInstitutionsByState,
    countOfBranchOfficesByState,
    // locationsOnly,
  ]);

  const [chartId] = React.useState(`usa-map-${Date.now()}`);

  React.useEffect(() => {
    return () => {
      // Clean up the chart instance when the component unmounts
      if (FusionCharts(chartId)) {
        FusionCharts(chartId).dispose();
      }
    };
  }, [chartId]);

  const usaMapChartProps = {
    type: "maps/usa",
    width: "700",
    height: "550",
    dataFormat: "JSON",
    dataSource: {
      chart: {
        caption: locationsOnly ? null : "Concentration of Deposits",
        subcaption: "Rollover a state for more information",
        entityfillhovercolor: "#EEEEEE",
        showlabels: "1",
        borderthickness: "0.4",
        theme: "fusion",
        basefontcolor: "#013a5d",
        entitytooltext: "<b>$lname</b>",
        numberPrefix: locationsOnly ? "" : "$",
        numberScaleValue: "1000,1000,1000",
        numberScaleUnit: "K,M,B",
      },
      colorrange: locationsOnly ? colorrangeLocationsOnly : colorrange,
      data: mapData,
    },
    events: {
      entityRollover: (event: KeyValue, data: KeyValue) =>
        handleMouseOver(
          data.label,
          data.shortLabel,
          stateElement,
          summaryOfDepositsElement,
          summaryOfDepositsByState,
          institutionsElement,
          countOfInstitutionsByState,
          branchOfficesElement,
          countOfBranchOfficesByState,
        ),
      entityRollout: () =>
        handleMouseOut(
          stateElement,
          summaryOfDepositsElement,
          totalSummaryOfDeposits,
          institutionsElement,
          totalInstitutions,
          branchOfficesElement,
          totalBranchOffices,
        ),
      // entityClick: function(event: any, data: any) {
      //   if (locationsOnly) {
      //     const newMap = data.label.toLowerCase().replace(/\s+/g, "");

      //     updateFilter &&
      //       updateFilter("stalp", convertNameToAbbreviation(newMap));
      //   }
      // },
      legendRangeUpdated: (eventObj: KeyValue) => handleRangeUpdated(eventObj),
    },
  };

  const showStatesAndTerritories = (minValue: number, maxValue: number) => {
    return (
      <>
        {usStatesAndTerritories.map((territory: KeyValue, index: number) => {
          return (
            <div
              key={index}
              onMouseOver={() =>
                handleMouseOver(
                  territory.altLabel ? territory.altLabel : territory.label,
                  territory.abbr,
                  stateElement,
                  summaryOfDepositsElement,
                  summaryOfDepositsByState,
                  institutionsElement,
                  countOfInstitutionsByState,
                  branchOfficesElement,
                  countOfBranchOfficesByState,
                )
              }
              onMouseOut={() =>
                handleMouseOut(
                  stateElement,
                  summaryOfDepositsElement,
                  totalSummaryOfDeposits,
                  institutionsElement,
                  totalInstitutions,
                  branchOfficesElement,
                  totalBranchOffices,
                )
              }
            >
              <Grid container wrap="nowrap">
                <Grid item>
                  <HeatBoxIcon
                    sumDepositValue={
                      locationsOnly
                        ? countOfBranchOfficesByState[territory.abbr]
                        : summaryOfDepositsByState[territory.abbr]
                    }
                    rangeMinValue={minValue}
                    rangeMaxValue={maxValue}
                  />
                </Grid>
                <Grid item className={classes.territory}>
                  <Typography color="primary" variant="body2">
                    {territory.label}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          );
        })}
      </>
    );
  };

  const cardContent = locationsOnly ? (
    <CardContent>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid item ref={branchOfficesElement}></Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid item>
            <Typography color="primary" variant="body1">
              Branch Offices
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  ) : (
    <CardContent>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid item ref={summaryOfDepositsElement}></Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item>
            <Typography color="primary" variant="body1">
              Total Deposits (Thousands USD)
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid item ref={institutionsElement}></Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid item ref={branchOfficesElement}></Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid item>
            <Typography color="primary" variant="body1">
              Institutions
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid item>
            <Typography color="primary" variant="body1">
              Branch Offices
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );

  return (
    <>
      <Typography color="primary" variant="h2" align="center">
        <Grid container spacing={2}>
          <Grid item lg={9} md={12}>
            {
              // @ts-ignore
              <ReactFC id={chartId} {...usaMapChartProps} />
            }
          </Grid>
          <Grid item lg={3} md={12}>
            <Paper elevation={3} id="map-data-info">
              <Card className={classes.cardDisplay}>
                <CardHeader
                  ref={stateElement}
                  className={classes.summaryDepositsTextBox}
                ></CardHeader>
                {cardContent}
              </Card>
            </Paper>
            <br />
            <Grid item>
              <Typography color="primary" className={classes.statesHeading}>
                States & Territories not depicted
              </Typography>
            </Grid>
            {showStatesAndTerritories(legendRangeMinValue, legendRangeMaxValue)}
          </Grid>
        </Grid>
      </Typography>
    </>
  );
}
